import React, { useState, useRef, useEffect, useCallback } from "react";
import { useBluelagoon } from "@bluelagoon";
import { useWorkflowExecution } from "../../../../context/WorkflowExecutionContext";
import {
    Database,
    TableIcon,
    File,
    MessageSquare,
    FileText,
    BarChart3,
    Globe,
    Image,
    Mail,
    Bot,
    Monitor,
    Search,
    Palette,
    Code,
    Link2,
} from "lucide-react";
import { useWorkflow } from "../../../../context/WorkflowContext";
import { useAuth } from "../../../../context/AuthContext";
import { FileSearchModal } from "../../../../components/FileSearchModal";
import { FunctionSearchModal } from "../../../../components/FunctionSearchModal";
import { AppLinkSearchModal } from "../../../../components/AppLinkSearchModal";
import { DatabaseAccessModal } from "../../../../components/DatabaseAccessModal";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { buildApiCallSnippet } from "@utils";

const MentionInput = ({
    value,
    onChange,
    onKeyPress,
    disabled,
    placeholder,
    onBrowserAutomation,
    inputStyle,
}) => {
    const { t } = useTranslation();
    const { isSuperAdmin, isFeatureEnabled, user } = useAuth();
    const companyId = user?.company?.id;
    // Allow shared databases for Innovator+ plans OR specific allowed companies
    const allowedCompanyIds = ["cmjoa3ng70jbyqp1ju559unxy"];
    const hasSharedDatabasesAccess =
        isFeatureEnabled("sharedDatabases") ||
        allowedCompanyIds.includes(companyId);
    const [showDropdown, setShowDropdown] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [selectedDatabase, setSelectedDatabase] = useState(null);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);
    const [dropdownPosition, setDropdownPosition] = useState({
        top: 0,
        bottom: 0,
        left: 0,
        useTop: true, // Whether to use top positioning or bottom positioning
    });

    const { workflow } = useWorkflow();
    const { workflowDatabases } = useWorkflowExecution();
    const [isFileSearchOpen, setIsFileSearchOpen] = useState(false);
    const [isFunctionSearchOpen, setIsFunctionSearchOpen] = useState(false);
    const [isAppLinkSearchOpen, setIsAppLinkSearchOpen] = useState(false);

    // Database access modal state
    const [showDatabaseAccessModal, setShowDatabaseAccessModal] =
        useState(false);
    const [pendingDatabaseAccess, setPendingDatabaseAccess] = useState(null);
    const [isRequestingAccess, setIsRequestingAccess] = useState(false);
    const databaseAccessResolveRef = useRef(null);

    // API hooks for database access
    const { call: checkDatabaseGrant } = useBluelagoon({
        method: "checkDatabaseAccessGrant",
    });
    const { call: requestDatabaseAccess } = useBluelagoon({
        method: "requestDatabaseAccess",
    });

    // Auto-resize textarea
    useEffect(() => {
        const textarea = inputRef.current;
        if (textarea) {
            // Reset height to auto to get the correct scrollHeight
            textarea.style.height = "auto";
            // Set height to scrollHeight to fit content, respecting max-height
            const newHeight = Math.min(textarea.scrollHeight, 500);
            textarea.style.height = `${newHeight}px`;
            // Enable scrolling if content exceeds max height
            textarea.style.overflowY =
                textarea.scrollHeight > 500 ? "auto" : "hidden";
        }
    }, [value]);

    // Fetch integrations using useBluelagoon
    const { call, response } = useBluelagoon({
        method: "integrationsList",
    });

    // Get connected integrations from response
    const integrations = React.useMemo(() => {
        const externalIntegrations =
            response?.data?.map((integration) => ({
                id: integration.id,
                name: integration.name || integration.type,
                type: "integration",
                category: "external",
            })) || [];

        // Check if user has Apify integration
        const hasApifyIntegration = externalIntegrations.some((integration) =>
            integration.name.toLowerCase().includes("apify")
        );

        // Trending integrations (always visible)
        const trendingIntegrations = [
            {
                id: "Chat GPT",
                name: "Chat GPT",
                type: "integration",
                category: "trending",
                icon: Bot,
            },
            {
                id: "Web Search",
                name: "Web Search",
                type: "integration",
                category: "trending",
                icon: Globe,
            },
        ];

        // Apify actors (only visible if user has Apify integration)

        // Categorized integrations
        const categorizedIntegrations = [
            // AI & Communication
            {
                id: "Send Email",
                name: "Send Email",
                type: "integration",
                category: "ai-communication",
                icon: Mail,
            },
            {
                id: "WhatsApp",
                name: "WhatsApp",
                type: "integration",
                category: "ai-communication",
                icon: MessageSquare,
            },
            {
                id: "imageAnalysis",
                name: "Image Analysis",
                type: "integration",
                category: "ai-communication",
                icon: Image,
            },
            {
                id: "Image Generation",
                name: "Image Generation",
                type: "integration",
                category: "ai-communication",
                icon: Palette,
            },
            ...(isSuperAdmin
                ? [
                      {
                          id: "Browser Automation",
                          name: "Browser Automation",
                          type: "integration",
                          category: "ai-communication",
                          icon: Monitor,
                      },
                  ]
                : []),

            // Document Processing
            // {
            //     id: "Document Editor",
            //     name: "AI Document Editor",
            //     type: "integration",
            //     category: "document-processing",
            //     icon: FileText,
            // },
            {
                id: "PDF To Text",
                name: "PDF To Text",
                type: "integration",
                category: "document-processing",
                icon: FileText,
            },
            {
                id: "PDF To Tables",
                name: "PDF To Tables",
                type: "integration",
                category: "document-processing",
                icon: FileText,
            },
            {
                id: "OCR",
                name: "OCR",
                type: "integration",
                category: "document-processing",
                icon: FileText,
            },
            {
                id: "AI Form Editor",
                name: "AI Form Editor",
                type: "integration",
                category: "document-processing",
                icon: FileText,
            },
            {
                id: "Generate Word Document",
                name: "Generate Word Document",
                type: "integration",
                category: "document-processing",
                icon: FileText,
            },
            {
                id: "Word Document Variables",
                name: "Word Document Variables",
                type: "integration",
                category: "document-processing",
                icon: FileText,
            },
            {
                id: "PowerPoint/Excel Template Variables",
                name: "PowerPoint/Excel Template Variables",
                type: "integration",
                category: "document-processing",
                icon: FileText,
            },
            {
                id: "Convert Documents to PDF",
                name: "Convert Documents to PDF",
                type: "integration",
                category: "document-processing",
                icon: FileText,
            },
            ...(workflow?.workflowType === "APP_V2"
                ? [
                      {
                          id: "export-to-presentation",
                          name: "@skills/export-to-presentation",
                          displayName: "Export to Presentation",
                          type: "integration",
                          category: "document-processing",
                          icon: FileText,
                      },
                  ]
                : []),

            // Data & Analytics
            {
                id: "Excel Reader",
                name: "Excel Reader",
                type: "integration",
                category: "data-analytics",
                icon: BarChart3,
            },
            {
                id: "Excel Generator",
                name: "Excel Generator",
                type: "integration",
                category: "data-analytics",
                icon: BarChart3,
            },

            {
                id: "Excel Template Generator",
                name: "Excel Template Generator",
                type: "integration",
                category: "data-analytics",
                icon: BarChart3,
            },
        ];

        const codeGenerationSkills =
            workflow?.workflowType === "APP_V2"
                ? [
                      {
                          id: "offline-pwa",
                          name: "@skills/offline-pwa",
                          displayName: "Offline Mode & PWA",
                          type: "integration",
                          category: "code-generation",
                          icon: Monitor,
                      },
                      {
                          id: "recharts",
                          name: "@skills/recharts",
                          displayName: "Recharts",
                          type: "integration",
                          category: "code-generation",
                          icon: BarChart3,
                      },
                      {
                          id: "react-leaflet",
                          name: "@skills/react-leaflet",
                          displayName: "React Leaflet Maps",
                          type: "integration",
                          category: "code-generation",
                          icon: Globe,
                      },
                      {
                          id: "fabricjs",
                          name: "@skills/fabricjs",
                          displayName: "Fabric.js Canvas Editor",
                          type: "integration",
                          category: "code-generation",
                          icon: Palette,
                      },
                      {
                          id: "threejs",
                          name: "@skills/threejs",
                          displayName: "Three.js 3D",
                          type: "integration",
                          category: "code-generation",
                          icon: Code,
                      },
                      {
                          id: "jspdf",
                          name: "@skills/jspdf",
                          displayName: "jsPDF",
                          type: "integration",
                          category: "code-generation",
                          icon: FileText,
                      },
                      {
                          id: "html-to-image",
                          name: "@skills/html-to-image",
                          displayName: "HTML to Image Export",
                          type: "integration",
                          category: "code-generation",
                          icon: Image,
                      },
                      {
                          id: "pptxgenjs",
                          name: "@skills/pptxgenjs",
                          displayName: "PowerPoint Generation",
                          type: "integration",
                          category: "code-generation",
                          icon: FileText,
                      },
                      {
                          id: "pptx-design-extractor",
                          name: "@skills/pptx-design-extractor",
                          displayName: "PowerPoint Design Extractor",
                          type: "integration",
                          category: "code-generation",
                          icon: Palette,
                      },
                      {
                          id: "create-agent-sdk",
                          name: "@skills/create-agent-sdk",
                          displayName: "Create Agent",
                          type: "integration",
                          category: "code-generation",
                          icon: Bot,
                      },
                      {
                          id: "exceljs",
                          name: "@skills/exceljs",
                          displayName: "ExcelJS",
                          type: "integration",
                          category: "code-generation",
                          icon: BarChart3,
                      },
                      {
                          id: "docxtemplater",
                          name: "@skills/docxtemplater",
                          displayName: "Document Templates",
                          type: "integration",
                          category: "code-generation",
                          icon: FileText,
                      },
                  ]
                : [
                      // Automation-compatible skills (server-side Deno)
                      {
                          id: "exceljs",
                          name: "@skills/exceljs",
                          displayName: "ExcelJS",
                          type: "integration",
                          category: "code-generation",
                          icon: BarChart3,
                      },
                      {
                          id: "pdfkit",
                          name: "@skills/pdfkit",
                          displayName: "PDFKit",
                          type: "integration",
                          category: "code-generation",
                          icon: FileText,
                      },
                      {
                          id: "docxtemplater",
                          name: "@skills/docxtemplater",
                          displayName: "Document Templates",
                          type: "integration",
                          category: "code-generation",
                          icon: FileText,
                      },
                      {
                          id: "lodash",
                          name: "@skills/lodash",
                          displayName: "Lodash",
                          type: "integration",
                          category: "code-generation",
                          icon: Code,
                      },
                      {
                          id: "date-fns",
                          name: "@skills/date-fns",
                          displayName: "date-fns",
                          type: "integration",
                          category: "code-generation",
                          icon: Code,
                      },
                      {
                          id: "moment-js",
                          name: "@skills/moment-js",
                          displayName: "Moment.js",
                          type: "integration",
                          category: "code-generation",
                          icon: Code,
                      },
                      {
                          id: "simple-statistics",
                          name: "@skills/simple-statistics",
                          displayName: "Simple Statistics",
                          type: "integration",
                          category: "code-generation",
                          icon: BarChart3,
                      },
                      {
                          id: "regression",
                          name: "@skills/regression",
                          displayName: "Regression Analysis",
                          type: "integration",
                          category: "code-generation",
                          icon: BarChart3,
                      },
                      {
                          id: "bwip-js",
                          name: "@skills/bwip-js",
                          displayName: "Barcode Generator",
                          type: "integration",
                          category: "code-generation",
                          icon: Code,
                      },
                      {
                          id: "browser-image-compression",
                          name: "@skills/browser-image-compression",
                          displayName: "Image Compression",
                          type: "integration",
                          category: "code-generation",
                          icon: Image,
                      },
                      {
                          id: "microsoft-graph",
                          name: "@skills/microsoft-graph",
                          displayName: "Microsoft Graph",
                          type: "integration",
                          category: "code-generation",
                          icon: Globe,
                      },
                  ];

        const patternSkills =
            workflow?.workflowType === "APP_V2"
                ? [
                      {
                          id: "multi-tenant",
                          name: "@skills/multi-tenant",
                          type: "pattern",
                          category: "patterns",
                          icon: Database,
                      },
                  ]
                : [];

        const apifyActors = [
            {
                id: "GdWCkxBtKWOsKjdch",
                name: "TikTok Scraper",
                type: "apify-actor",
                category: "apify",
                icon: BarChart3,
                actorId: "GdWCkxBtKWOsKjdch",
                disabled: !hasApifyIntegration,
            },
            {
                id: "nwua9Gu5YrADL7ZDj",
                name: "Google Maps Scraper",
                type: "apify-actor",
                category: "apify",
                icon: BarChart3,
                actorId: "nwua9Gu5YrADL7ZDj",
                disabled: !hasApifyIntegration,
            },
            {
                id: "shu8hvrXbJbY3Eb9W",
                name: "Instagram Scrapper",
                type: "apify-actor",
                category: "apify",
                icon: BarChart3,
                actorId: "shu8hvrXbJbY3Eb9W",
                disabled: !hasApifyIntegration,
            },
            {
                id: "XVDTQc4a7MDTqSTMJ",
                name: "Amazon Scraper",
                type: "apify-actor",
                category: "apify",
                icon: BarChart3,
                actorId: "XVDTQc4a7MDTqSTMJ",
                disabled: !hasApifyIntegration,
            },
            {
                id: "other-actors",
                name: "Other Actors",
                type: "apify-other",
                category: "apify",
                icon: BarChart3,
                disabled: !hasApifyIntegration,
                description: !hasApifyIntegration
                    ? "Requires Apify Integration"
                    : "Apify Integration",
            },
        ];

        // Add databases and tables - only include active workflow databases
        const activeWorkflowDatabases =
            workflowDatabases?.filter(
                (db) => db.isActiveWorkflowDatabase === true
            ) || [];
        const inactiveWorkflowDatabases =
            workflowDatabases?.filter(
                (db) => db.isActiveWorkflowDatabase !== true
            ) || [];

        // Process active workflow databases individually
        // disable this for now
        // const activeDatabaseItems = activeWorkflowDatabases.map((database) => ({
        //     id: database.id,
        //     name: database.name,
        //     type: "database",
        //     category: "databases",
        //     isActiveWorkflowDatabase: true,
        //     tables:
        //         database.tables?.map((table) => ({
        //             id: table.id,
        //             name: table.name,
        //             type: "table",
        //             databaseName: database.name,
        //             databaseId: database.id,
        //             category: "databases",
        //             isActiveWorkflowDatabase: true,
        //         })) || [],
        // }));

        // Group inactive databases under a single entry if there are any
        // Only show if user has sharedDatabases feature enabled (Innovator plan and above)
        const inactiveDatabaseItems =
            hasSharedDatabasesAccess && inactiveWorkflowDatabases.length > 0
                ? [
                      {
                          id: "inactive-databases",
                          name: t(
                              "mentions.sharedDatabases",
                              "Shared Databases"
                          ),
                          type: "database-group",
                          category: "databases",
                          isBeta: true,
                          databases: inactiveWorkflowDatabases.map(
                              (database) => ({
                                  id: database.id,
                                  name: database.name,
                                  type: "database",
                                  category: "databases",
                                  isActiveWorkflowDatabase: false,
                                  tables:
                                      database.tables?.map((table) => ({
                                          id: table.id,
                                          name: table.name,
                                          type: "table",
                                          databaseName: database.name,
                                          databaseId: database.id,
                                          category: "databases",
                                          isActiveWorkflowDatabase: false,
                                      })) || [],
                              })
                          ),
                      },
                  ]
                : [];

        // Create tables extras for active databases only
        // const tablesExtras = activeDatabaseItems.flatMap(
        //     (database) =>
        //         database.tables.map((table) => ({
        //             id: table.id,
        //             name: `Database: ${database.name} Table: ${table.name}`,
        //             type: "table",
        //             databaseName: database.name,
        //             category: "databases",
        //             isExtra: true,
        //         })) || []
        // );

        const searchFilesOption = {
            id: "search-files-action",
            name: "Search Files...",
            type: "action",
            category: "files",
            icon: Search,
        };

        const searchFunctionsOption = {
            id: "search-functions-action",
            name: t("mentions.sharedFunctions", "Shared Functions"),
            type: "action",
            category: "shared-functions",
            icon: Bot,
        };

        const linkedAppsOption =
            workflow?.workflowType === "APP_V2"
                ? [
                      {
                          id: "search-apps-action",
                          name: "Linked Apps",
                          type: "action",
                          category: "linked-apps",
                          icon: Link2,
                      },
                  ]
                : [];

        return [
            ...linkedAppsOption,
            ...inactiveDatabaseItems,
            searchFunctionsOption,
            searchFilesOption,
            ...trendingIntegrations,
            ...externalIntegrations,
            ...categorizedIntegrations,
            ...codeGenerationSkills,
            ...patternSkills,
            ...apifyActors,
            // ...activeDatabaseItems,
            // ...tablesExtras,
        ];
    }, [response, workflowDatabases, workflow, t, hasSharedDatabasesAccess]);

    // Group items by category for display
    const groupedItems = React.useMemo(() => {
        const groups = {
            "linked-apps": {
                name: t("mentions.linkedApps", "Linked Apps"),
                items: [],
            },
            databases: {
                name: t("mentions.databases", "Databases"),
                items: [],
            },
            "shared-functions": {
                name: t("mentions.sharedFunctions", "Shared Functions"),
                items: [],
            },
            files: { name: t("mentions.files", "Files"), items: [] },
            trending: { name: t("mentions.trending", "Trending"), items: [] },

            "ai-communication": {
                name: t("mentions.aiCommunication", "AI & Communication"),
                items: [],
            },
            "document-processing": {
                name: t("mentions.documentProcessing", "Document Processing"),
                items: [],
            },
            "data-analytics": {
                name: t("mentions.dataAnalytics", "Data & Analytics"),
                items: [],
            },
            "code-generation": {
                name:
                    workflow?.workflowType === "APP_V2"
                        ? t("mentions.codeGeneration", "App Development")
                        : t("mentions.automationSkills", "Automation Skills"),
                items: [],
            },
            patterns: {
                name: t("mentions.patterns", "Patterns"),
                items: [],
            },

            external: {
                name: t(
                    "mentions.externalIntegrations",
                    "External Integrations"
                ),
                items: [],
            },
            apify: {
                name: t("mentions.apifyActors", "Apify Actors"),
                items: [],
            },
        };

        // Handle database group selection
        if (
            selectedDatabase === t("mentions.databases", "Databases") ||
            selectedDatabase ===
                t("mentions.sharedDatabases", "Shared Databases")
        ) {
            const databaseGroup = integrations.find(
                (i) => i.type === "database-group"
            );
            const items = databaseGroup?.databases || [];
            if (searchText === "") {
                groups.databases.items = items;
            } else {
                groups.databases.items = items.filter((db) =>
                    db.name.toLowerCase().includes(searchText.toLowerCase())
                );
            }
        } else if (selectedDatabase) {
            // Show tables for selected database
            let database = integrations.find(
                (i) => i.type === "database" && i.name === selectedDatabase
            );

            if (!database) {
                const databaseGroup = integrations.find(
                    (i) => i.type === "database-group"
                );
                database = databaseGroup?.databases.find(
                    (db) => db.name === selectedDatabase
                );
            }

            const tables =
                database?.tables?.filter((table) =>
                    table.name.toLowerCase().includes(searchText.toLowerCase())
                ) || [];
            groups.databases.items = tables;
        } else {
            // Group all items by category
            integrations.forEach((item) => {
                if (item.type === "table" && !item.isExtra) return; // Skip individual tables

                const category = item.category || "external";
                if (groups[category]) {
                    if (
                        searchText === "" ||
                        (item.displayName || item.name)
                            .toLowerCase()
                            .includes(searchText.toLowerCase())
                    ) {
                        groups[category].items.push(item);
                    }
                }
            });

            // Add table extras to databases category
            const tableExtras = integrations.filter(
                (item) => item.type === "table" && item.isExtra
            );
            if (tableExtras.length > 0) {
                groups.databases.items.push(...tableExtras);
            }
        }

        // Remove empty groups
        Object.keys(groups).forEach((key) => {
            if (groups[key].items.length === 0) {
                delete groups[key];
            }
        });

        return groups;
    }, [searchText, selectedDatabase, integrations, t, workflow?.workflowType]);

    // Flatten items for keyboard navigation
    const flattenedItems = React.useMemo(() => {
        const items = [];
        Object.values(groupedItems).forEach((group) => {
            items.push(...group.items);
        });
        return items;
    }, [groupedItems]);

    // Create a mapping from flat index to actual item for proper navigation
    const getItemByFlatIndex = (flatIndex) => {
        return flattenedItems[flatIndex];
    };

    // Improved dropdown positioning function with bottom anchoring
    const updateDropdownPosition = () => {
        if (!inputRef.current) return;

        const textarea = inputRef.current;
        const caretCoords = getCaretCoordinates(
            textarea,
            textarea.selectionEnd
        );

        // Get viewport dimensions
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Get textarea's position relative to the viewport
        const textareaRect = textarea.getBoundingClientRect();

        // Calculate absolute position of caret in viewport
        const absoluteTop = textareaRect.top + caretCoords.top;
        const absoluteLeft = textareaRect.left + caretCoords.left;

        // Dropdown dimensions
        const dropdownWidth = 300;
        const dropdownHeight = 280; // Max height, actual height may be less

        // Calculate available space above and below the input
        const caretHeight = caretCoords.height || 20; // Fallback height
        const spaceBelow = viewportHeight - (absoluteTop + caretHeight);
        const spaceAbove = absoluteTop;

        // Determine if we should position above or below
        const useTopPositioning =
            spaceBelow >= dropdownHeight || spaceBelow >= spaceAbove;

        // Calculate positions
        let top, bottom, left;

        if (useTopPositioning) {
            // Position below the caret (using top positioning)
            top = caretCoords.top + caretHeight;
        } else {
            // Position above the caret (using bottom positioning)
            bottom = textarea.clientHeight - caretCoords.top;
        }

        left = caretCoords.left;

        // Check if dropdown would go beyond right edge of viewport
        if (absoluteLeft + dropdownWidth > viewportWidth) {
            // Try to position to the left of the caret
            if (absoluteLeft - dropdownWidth > 0) {
                left = caretCoords.left - dropdownWidth;
            } else {
                // Not enough space on either side, align with left edge of textarea
                // but ensure it stays within viewport
                left = Math.max(
                    0,
                    Math.min(
                        textareaRect.width - dropdownWidth,
                        caretCoords.left
                    )
                );
            }
        }

        // Ensure dropdown doesn't go beyond left edge of textarea
        left = Math.max(0, left);

        // Ensure dropdown doesn't go beyond right edge of textarea
        if (left + dropdownWidth > textarea.clientWidth) {
            left = Math.max(0, textarea.clientWidth - dropdownWidth);
        }

        // Set position with smart top/bottom positioning
        setDropdownPosition({
            top: top || 0,
            bottom: bottom || 0,
            left,
            useTop: useTopPositioning,
        });
    };

    // Add this helper function to calculate caret coordinates
    const getCaretCoordinates = (element, position) => {
        const div = document.createElement("div");
        const style = div.style;
        const computed = window.getComputedStyle(element);

        // Copy the textarea's style to the div
        style.whiteSpace = "pre-wrap";
        style.wordWrap = "break-word";
        style.position = "absolute";
        style.visibility = "hidden";
        style.width = computed.width;
        style.height = computed.height;
        style.padding = computed.padding;
        style.border = computed.border;
        style.font = computed.font;
        style.lineHeight = computed.lineHeight;

        // Create a span at the caret position
        const text = element.value.substring(0, position);
        const span = document.createElement("span");
        div.textContent = text;
        div.appendChild(span);
        document.body.appendChild(div);

        const coordinates = {
            top: span.offsetTop,
            left: span.offsetLeft,
            height: parseInt(computed.lineHeight),
        };

        document.body.removeChild(div);
        return coordinates;
    };

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        const cursorPosition = e.target.selectionStart;
        onChange(newValue);

        // Find the word at the cursor position
        const wordAtCursor = getWordAtPosition(newValue, cursorPosition);
        if (wordAtCursor.startsWith("@") && !wordAtCursor.includes("]")) {
            setSearchText(wordAtCursor.slice(1));
            setShowDropdown(true);
            setSelectedIndex(0);
            // Update dropdown position after state changes
            setTimeout(updateDropdownPosition, 0);
        } else {
            setShowDropdown(false);
            setSelectedDatabase(null);
        }
    };

    // Helper function to get the word at a specific position
    const getWordAtPosition = (text, position) => {
        // Find the start of the word (look backwards for space or start of string)
        let wordStart = position;
        while (
            wordStart > 0 &&
            text[wordStart - 1] !== " " &&
            text[wordStart - 1] !== "\n"
        ) {
            wordStart--;
        }

        // Find the end of the word (look forwards for space or end of string)
        let wordEnd = position;
        while (
            wordEnd < text.length &&
            text[wordEnd] !== " " &&
            text[wordEnd] !== "\n"
        ) {
            wordEnd++;
        }

        return text.substring(wordStart, wordEnd);
    };

    // Helper function to replace word at cursor position
    const replaceWordAtCursor = (text, cursorPosition, newWord) => {
        // Find the start of the word (look backwards for space or start of string)
        let wordStart = cursorPosition;
        while (
            wordStart > 0 &&
            text[wordStart - 1] !== " " &&
            text[wordStart - 1] !== "\n"
        ) {
            wordStart--;
        }

        // Find the end of the word (look forwards for space or end of string)
        let wordEnd = cursorPosition;
        while (
            wordEnd < text.length &&
            text[wordEnd] !== " " &&
            text[wordEnd] !== "\n"
        ) {
            wordEnd++;
        }

        // Replace the word and return the new text
        const beforeWord = text.substring(0, wordStart);
        const afterWord = text.substring(wordEnd);
        return {
            newText: beforeWord + newWord + " " + afterWord,
            newCursorPosition: beforeWord.length + newWord.length + 1,
        };
    };

    // Handle cursor position changes (clicks, arrow keys, etc.)
    const handleCursorPositionChange = (e) => {
        const text = e.target.value;
        const cursorPosition = e.target.selectionStart;

        // Find the word at the new cursor position
        const wordAtCursor = getWordAtPosition(text, cursorPosition);
        if (wordAtCursor.startsWith("@") && !wordAtCursor.includes("]")) {
            setSearchText(wordAtCursor.slice(1));
            setShowDropdown(true);
            setSelectedIndex(0);
            // Update dropdown position after state changes
            setTimeout(updateDropdownPosition, 0);
        } else {
            setShowDropdown(false);
            setSelectedDatabase(null);
        }
    };

    // Check if database access is needed and request it
    const checkAndRequestDatabaseAccess = useCallback(
        async (database, table) => {
            if (!workflow?.id || !database) return true;

            try {
                // Check if we already have access
                const accessCheck = await checkDatabaseGrant({
                    workflowId: workflow.id,
                    databaseId: database.id,
                    tableName: table?.name,
                });

                if (accessCheck?.data?.hasAccess) {
                    return true;
                }

                // If no access, show modal to request
                setPendingDatabaseAccess({ database, table });
                setShowDatabaseAccessModal(true);

                // Return a promise that resolves when user handles the modal
                return new Promise((resolve) => {
                    databaseAccessResolveRef.current = resolve;
                });
            } catch (error) {
                console.error("Error checking database access:", error);
                // On error, proceed but log warning
                return true;
            }
        },
        [workflow?.id, checkDatabaseGrant]
    );

    const handleGrantDatabaseAccess = useCallback(
        async (accessType = "read") => {
            if (!pendingDatabaseAccess || !workflow?.id) return;

            setIsRequestingAccess(true);
            try {
                const grant = await requestDatabaseAccess({
                    grantedWorkflowId: workflow.id,
                    databaseId: pendingDatabaseAccess.database.id,
                    tableName: pendingDatabaseAccess.table?.name,
                    accessType,
                });

                if (grant?.data?.id) {
                    toast.success(
                        t(
                            "databaseAccess.accessGranted",
                            "Database access granted"
                        )
                    );
                    if (databaseAccessResolveRef.current) {
                        databaseAccessResolveRef.current(true);
                    }
                } else {
                    throw new Error("Failed to grant access");
                }
            } catch (error) {
                console.error("Failed to request database access:", error);
                toast.error(
                    t(
                        "databaseAccess.accessFailed",
                        "Failed to request database access"
                    )
                );
                if (databaseAccessResolveRef.current) {
                    databaseAccessResolveRef.current(false);
                }
            } finally {
                setIsRequestingAccess(false);
                setShowDatabaseAccessModal(false);
                setPendingDatabaseAccess(null);
                databaseAccessResolveRef.current = null;
            }
        },
        [pendingDatabaseAccess, workflow?.id, requestDatabaseAccess, t]
    );

    const handleDenyDatabaseAccess = useCallback(() => {
        if (databaseAccessResolveRef.current) {
            databaseAccessResolveRef.current(false);
        }
        setShowDatabaseAccessModal(false);
        setPendingDatabaseAccess(null);
        databaseAccessResolveRef.current = null;
    }, []);

    const handleSelectItem = async (item) => {
        // Prevent selection of disabled items
        if (item.disabled) {
            return;
        }

        if (item.type === "database" || item.type === "database-group") {
            setSelectedDatabase(item.name);
            setSearchText("");
            setSelectedIndex(0);
            return;
        }

        if (item.id === "search-functions-action") {
            setIsFunctionSearchOpen(true);
            setShowDropdown(false);
            return;
        }

        if (item.id === "search-apps-action") {
            setIsAppLinkSearchOpen(true);
            setShowDropdown(false);
            return;
        }

        if (item.type === "apify-other") {
            // Handle "Other Actors" selection - could open a modal or navigate to actor browser
            const words = value.split(" ");
            const mention = `@[My Apify Integration]`;
            words[words.length - 1] = mention;
            onChange(words.join(" ") + " ");
            setShowDropdown(false);
            setSelectedDatabase(null);
            inputRef.current?.focus();
            return;
        }

        // Handle Browser Automation selection
        if (item.name === "Browser Automation") {
            const words = value.split(" ");
            const mention = `@[Browser Automation]`;
            words[words.length - 1] = mention;
            onChange(words.join(" ") + " ");
            setShowDropdown(false);
            setSelectedDatabase(null);

            // Trigger browser automation callback if provided
            if (
                onBrowserAutomation &&
                typeof onBrowserAutomation === "function"
            ) {
                // Extract the instruction from the input value
                const instruction = value.replace(/@\w*$/, "").trim();
                onBrowserAutomation(instruction);
            }

            inputRef.current?.focus();
            return;
        }

        if (item.id === "search-files-action") {
            setIsFileSearchOpen(true);
            setShowDropdown(false);
            return;
        }

        // Check if this is a table from a non-active database (needs access grant)
        if (
            item.type === "table" &&
            !item.isExtra &&
            !item.isActiveWorkflowDatabase
        ) {
            // Find the database object
            const databaseGroup = integrations.find(
                (i) => i.type === "database-group"
            );
            const database = databaseGroup?.databases?.find(
                (db) => db.name === item.databaseName
            );

            if (database) {
                const hasAccess = await checkAndRequestDatabaseAccess(
                    database,
                    item
                );
                if (!hasAccess) {
                    // User denied access, don't insert the mention
                    return;
                }
            }
        }

        const words = value.split(" ");
        // Get current cursor position
        const cursorPosition = inputRef.current?.selectionStart || value.length;
        let mention;

        if (item.type === "apify-other") {
            mention = `@[My Apify Integration]`;
        } else if (item.type === "apify-actor") {
            // Include actor ID for Apify actors
            mention = `@[${item.name} - Apify Actor: ${item.actorId}]`;
        } else if (item.type === "table" && !item.isExtra) {
            mention = `@[Database: ${item.databaseName} Table: ${item.name}]`;
        } else {
            mention = `@[${item.name}]`;
        }

        // Replace the word at cursor position
        const { newText, newCursorPosition } = replaceWordAtCursor(
            value,
            cursorPosition,
            mention
        );
        onChange(newText);
        setShowDropdown(false);
        setSelectedDatabase(null);

        // Set cursor position after the inserted mention
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
                inputRef.current.setSelectionRange(
                    newCursorPosition,
                    newCursorPosition
                );
            }
        }, 0);
    };

    const handleKeyDown = (e) => {
        if (!showDropdown) {
            if (onKeyPress) {
                onKeyPress(e);
            }
            return;
        }

        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                setSelectedIndex((prev) => {
                    let nextIndex = prev + 1;
                    // Skip disabled items
                    while (
                        nextIndex < flattenedItems.length &&
                        flattenedItems[nextIndex].disabled
                    ) {
                        nextIndex++;
                    }
                    return nextIndex < flattenedItems.length ? nextIndex : prev;
                });
                break;
            case "ArrowUp":
                e.preventDefault();
                setSelectedIndex((prev) => {
                    let nextIndex = prev - 1;
                    // Skip disabled items
                    while (
                        nextIndex >= 0 &&
                        flattenedItems[nextIndex].disabled
                    ) {
                        nextIndex--;
                    }
                    return nextIndex >= 0 ? nextIndex : prev;
                });
                break;
            case "Enter":
                e.preventDefault();
                if (
                    flattenedItems[selectedIndex] &&
                    !flattenedItems[selectedIndex].disabled
                ) {
                    handleSelectItem(flattenedItems[selectedIndex]);
                }
                break;
            case "Escape":
                setShowDropdown(false);
                setSelectedDatabase(null);
                break;
            case "Backspace":
                if (selectedDatabase && !searchText) {
                    e.preventDefault();
                    setSelectedDatabase(null);
                } else if (onKeyPress) {
                    onKeyPress(e);
                }
                break;
            default:
                if (onKeyPress) {
                    onKeyPress(e);
                }
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                !inputRef.current?.contains(event.target)
            ) {
                setShowDropdown(false);
                setSelectedDatabase(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        call({ limit: 10000, isActive: true });
    }, []);

    // Update dropdown position whenever search text changes
    useEffect(() => {
        if (showDropdown) {
            updateDropdownPosition();
        }
    }, [showDropdown, searchText]);

    // Also update position when filtered items change
    useEffect(() => {
        if (showDropdown) {
            updateDropdownPosition();
        }
    }, [flattenedItems]);

    // Add auto-scrolling for dropdown items
    useEffect(() => {
        if (showDropdown && dropdownRef.current) {
            // Get the items container
            const itemsContainer =
                dropdownRef.current.querySelector(".overflow-y-auto");
            if (!itemsContainer) return;

            // Find the selected element by looking for the item with the selected index
            const allItems =
                itemsContainer.querySelectorAll("[data-item-index]");
            const selectedElement = Array.from(allItems).find(
                (element) =>
                    parseInt(element.getAttribute("data-item-index")) ===
                    selectedIndex
            );

            if (!selectedElement) return;

            // Calculate scroll positions
            const containerHeight = itemsContainer.clientHeight;
            const elementTop = selectedElement.offsetTop;
            const elementHeight = selectedElement.offsetHeight;
            const elementBottom = elementTop + elementHeight;

            // Check if element is outside the visible area
            if (elementBottom > itemsContainer.scrollTop + containerHeight) {
                // Scroll down to show the element
                itemsContainer.scrollTop = elementBottom - containerHeight;
            } else if (elementTop < itemsContainer.scrollTop) {
                // Scroll up to show the element
                itemsContainer.scrollTop = elementTop;
            }
        }
    }, [selectedIndex, showDropdown]);

    // Helper function to get icon for item
    const getItemIcon = (item) => {
        if (item.icon) {
            const IconComponent = item.icon;
            return <IconComponent className="h-4 w-4" />;
        }

        switch (item.type) {
            case "database":
            case "database-group":
                return <Database className="h-4 w-4" />;
            case "table":
                return <TableIcon className="h-4 w-4" />;
            case "file":
                return <File className="h-4 w-4" />;
            case "integration":
                return <Bot className="h-4 w-4" />;
            default:
                return <Bot className="h-4 w-4" />;
        }
    };

    // Helper function to get category icon
    const getCategoryIcon = (category) => {
        switch (category) {
            case "trending":
                return <Bot className="h-4 w-4" />;
            case "apify":
                return <BarChart3 className="h-4 w-4" />;
            case "ai-communication":
                return <MessageSquare className="h-4 w-4" />;
            case "document-processing":
                return <FileText className="h-4 w-4" />;
            case "data-analytics":
                return <BarChart3 className="h-4 w-4" />;
            case "code-generation":
                return <Code className="h-4 w-4" />;
            case "external":
                return <Globe className="h-4 w-4" />;
            case "databases":
                return <Database className="h-4 w-4" />;
            case "shared-functions":
                return <Code className="h-4 w-4" />;
            case "files":
                return <File className="h-4 w-4" />;
            default:
                return <Bot className="h-4 w-4" />;
        }
    };

    const handleFunctionSelect = (fn) => {
        const cursorPosition = inputRef.current?.selectionStart || value.length;

        let insertText;
        if (fn.apiAccessData) {
            // Non-APP_V2: insert code snippet with API URL and secret
            const { apiUrl, secretName, functionName } = fn.apiAccessData;
            insertText = buildApiCallSnippet(apiUrl, secretName, functionName);
        } else {
            // APP_V2: insert @[Function: WrapperName] mention
            insertText = `@[functions/${fn.wrapperName}]`;
        }

        const { newText, newCursorPosition } = replaceWordAtCursor(
            value,
            cursorPosition,
            insertText
        );
        onChange(newText);
        setIsFunctionSearchOpen(false);
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
                inputRef.current.setSelectionRange(
                    newCursorPosition,
                    newCursorPosition
                );
            }
        }, 0);
    };

    const handleFileSelect = (file) => {
        // Get current cursor position
        const cursorPosition = inputRef.current?.selectionStart || value.length;
        // Use file URL if available, otherwise use file name
        const fileReference = file.url || `@[${file.name}]`;

        // Replace the word at cursor position
        const { newText, newCursorPosition } = replaceWordAtCursor(
            value,
            cursorPosition,
            fileReference
        );
        onChange(newText);
        setIsFileSearchOpen(false);

        // Set cursor position after the inserted mention
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
                inputRef.current.setSelectionRange(
                    newCursorPosition,
                    newCursorPosition
                );
            }
        }, 0);
    };

    const handleAppLinkSelect = ({ appName, pageName, domain }) => {
        const cursorPosition = inputRef.current?.selectionStart || value.length;
        const mention = `@[App: ${appName} | Page: ${pageName} | Domain: ${domain}]`;
        const { newText, newCursorPosition } = replaceWordAtCursor(
            value,
            cursorPosition,
            mention
        );
        onChange(newText);
        setIsAppLinkSearchOpen(false);
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
                inputRef.current.setSelectionRange(
                    newCursorPosition,
                    newCursorPosition
                );
            }
        }, 0);
    };

    return (
        <div className="relative w-full">
            <textarea
                ref={inputRef}
                value={value}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onClick={(e) => {
                    updateDropdownPosition();
                    handleCursorPositionChange(e);
                }}
                onKeyUp={(e) => {
                    // Only handle cursor position changes for non-navigation keys
                    if (
                        !showDropdown ||
                        !["ArrowDown", "ArrowUp", "Enter", "Escape"].includes(
                            e.key
                        )
                    ) {
                        handleCursorPositionChange(e);
                    }
                }}
                onScroll={updateDropdownPosition}
                placeholder={placeholder}
                disabled={disabled}
                className="p-2 w-full bg-transparent resize-none whitespace-pre-wrap outline-none text-sm"
                style={inputStyle}
                rows={2}
                data-guided="chat-input"
            />

            <FileSearchModal
                open={isFileSearchOpen}
                onOpenChange={setIsFileSearchOpen}
                onSelect={handleFileSelect}
            />

            <FunctionSearchModal
                open={isFunctionSearchOpen}
                onOpenChange={setIsFunctionSearchOpen}
                onSelect={handleFunctionSelect}
                workflowId={workflow?.id}
                workflowType={workflow?.workflowType}
            />

            <AppLinkSearchModal
                open={isAppLinkSearchOpen}
                onOpenChange={setIsAppLinkSearchOpen}
                onSelect={handleAppLinkSelect}
                currentWorkflowId={workflow?.id}
            />

            <DatabaseAccessModal
                isOpen={showDatabaseAccessModal}
                onClose={handleDenyDatabaseAccess}
                database={pendingDatabaseAccess?.database}
                table={pendingDatabaseAccess?.table}
                onGrant={handleGrantDatabaseAccess}
                isGranting={isRequestingAccess}
            />

            {showDropdown && (
                <div
                    ref={dropdownRef}
                    className="absolute bg-white border rounded-lg shadow-lg z-50"
                    style={{
                        ...(dropdownPosition.useTop
                            ? { top: `${dropdownPosition.top}px` }
                            : { bottom: `${dropdownPosition.bottom}px` }),
                        left: `${dropdownPosition.left}px`,
                        width: "380px",
                    }}
                >
                    {selectedDatabase && (
                        <div className="px-6 py-3 bg-gray-100 flex items-center gap-3 border-b">
                            <Database className="h-5 w-5" />
                            <span className="font-medium text-gray-800">
                                {selectedDatabase}
                            </span>
                        </div>
                    )}

                    <div
                        className="overflow-y-auto"
                        style={{ maxHeight: "360px" }}
                    >
                        {Object.keys(groupedItems).length > 0 ? (
                            Object.entries(groupedItems).map(
                                ([category, group]) => (
                                    <div key={category}>
                                        {!selectedDatabase && (
                                            <div className="px-6 py-3 bg-gray-50 border-b flex items-center gap-3">
                                                {getCategoryIcon(category)}
                                                <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                                                    {group.name}
                                                </span>
                                            </div>
                                        )}
                                        {group.items.map((item, index) => {
                                            const globalIndex =
                                                flattenedItems.indexOf(item);
                                            const isSelected =
                                                globalIndex === selectedIndex;

                                            return (
                                                <div
                                                    key={`${item.type}-${item.id}`}
                                                    data-item-index={
                                                        globalIndex
                                                    }
                                                    onClick={() =>
                                                        !item.disabled &&
                                                        handleSelectItem(item)
                                                    }
                                                    className={`px-6 py-3 flex items-center gap-4 text-sm border-b border-gray-100 last:border-b-0 transition-colors duration-150
                                                        ${
                                                            item.disabled
                                                                ? "opacity-50 cursor-not-allowed bg-gray-50"
                                                                : "cursor-pointer hover:bg-gray-50"
                                                        }
                                                        ${
                                                            isSelected &&
                                                            !item.disabled
                                                                ? "bg-blue-50 text-blue-600 border-blue-200"
                                                                : ""
                                                        }
                                                        ${
                                                            item.type ===
                                                            "apify-other"
                                                                ? "bg-gradient-to-r from-purple-50 to-blue-50 hover:from-purple-100 hover:to-blue-100"
                                                                : ""
                                                        }`}
                                                >
                                                    <div className="flex-shrink-0">
                                                        {getItemIcon(item)}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div
                                                            className={`font-medium truncate ${
                                                                item.disabled
                                                                    ? "text-gray-400"
                                                                    : item.type ===
                                                                      "apify-other"
                                                                    ? "text-purple-700"
                                                                    : "text-gray-900"
                                                            }`}
                                                        >
                                                            {item.displayName ||
                                                                item.name}
                                                            {item.isBeta && (
                                                                <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium">
                                                                    BETA
                                                                </span>
                                                            )}
                                                            {item.disabled && (
                                                                <span className="ml-2 text-xs text-gray-400">
                                                                    (Disabled)
                                                                </span>
                                                            )}
                                                        </div>
                                                        {item.type ===
                                                            "table" &&
                                                            item.databaseName && (
                                                                <div className="text-xs text-gray-500 truncate mt-1">
                                                                    {
                                                                        item.databaseName
                                                                    }
                                                                </div>
                                                            )}
                                                        {item.type ===
                                                            "apify-other" &&
                                                            item.description && (
                                                                <div className="text-xs text-purple-600 truncate mt-1">
                                                                    {
                                                                        item.description
                                                                    }
                                                                </div>
                                                            )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )
                            )
                        ) : (
                            <div className="px-6 py-12 text-center text-gray-500">
                                <Bot className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                                <div className="text-base font-medium mb-2">
                                    {t(
                                        "mentions.noMatches",
                                        "No matches found"
                                    )}
                                </div>
                                <div className="text-sm text-gray-400">
                                    {t(
                                        "mentions.tryDifferentSearch",
                                        "Try a different search term"
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MentionInput;
